import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Spin, Typography, Select, Switch, Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import EmptyState from '../components/EmptyState';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ContentFormData {
  website_id: string;
  type: 'page' | 'post' | 'product';
  title: string;
  slug: string;
  content: string;
  meta_title: string;
  meta_description: string;
  published: boolean;
}

interface Website {
  id: string;
  domain: string;
}

const ContentForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [websitesLoading, setWebsitesLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    fetchWebsites();
    
    if (id) {
      setIsEdit(true);
      fetchContentData(id);
    }
  }, [id]);

  const fetchWebsites = async () => {
    setWebsitesLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo`);
      if (response.data.success) {
        setWebsites(response.data.data);
      } else {
        message.error(response.data.message || '获取网站列表失败');
      }
    } catch (error: any) {
      message.error('获取网站列表时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setWebsitesLoading(false);
    }
  };

  const fetchContentData = async (contentId: string) => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/contents/${contentId}`);
      if (response.data.success) {
        form.setFieldsValue({
          ...response.data.data,
          published: Boolean(response.data.data.published),
        });
      } else {
        message.error(response.data.message || '获取内容数据失败');
        navigate('/contents');
      }
    } catch (error: any) {
      message.error('获取内容数据时出错: ' + (error.response?.data?.message || error.message));
      navigate('/contents');
    } finally {
      setInitialLoading(false);
    }
  };

  const onFinish = async (values: ContentFormData) => {
    setLoading(true);
    try {
      let response;
      if (isEdit) {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/contents/${id}`, values);
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/contents`, values);
      }

      if (response.data.success) {
        message.success(response.data.message || (isEdit ? '内容更新成功' : '内容创建成功'));
        navigate('/contents');
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        // 显示表单验证错误
        error.response.data.errors.forEach((err: any) => {
          message.error(`${err.path}: ${err.message}`);
        });
      } else {
        message.error('操作失败: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    // 如果用户尚未手动设置slug，自动生成slug
    const slug = form.getFieldValue('slug');
    if (!slug && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
        .replace(/(^-|-$)/g, '');
      form.setFieldValue('slug', generatedSlug);
    }
  };

  if (initialLoading || websitesLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="加载数据..." />
      </div>
    );
  }

  if (websites.length === 0 && !isEdit) {
    return (
      <EmptyState
        title="未找到网站"
        description="请先创建一个网站，然后再添加内容"
        createPath="/websites/new"
        createText="创建网站"
      />
    );
  }

  return (
    <div>
      <Title level={2}>{isEdit ? '编辑内容' : '添加内容'}</Title>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            type: 'page',
            published: false,
            meta_title: '',
            meta_description: '',
          }}
        >
          {!isEdit && (
            <Form.Item
              name="website_id"
              label="所属网站"
              rules={[{ required: true, message: '请选择网站' }]}
            >
              <Select placeholder="请选择网站">
                {websites.map(website => (
                  <Option key={website.id} value={website.id}>{website.domain}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {!isEdit && (
            <Form.Item
              name="type"
              label="内容类型"
              rules={[{ required: true, message: '请选择内容类型' }]}
            >
              <Select placeholder="请选择内容类型">
                <Option value="page">页面</Option>
                <Option value="post">文章</Option>
                <Option value="product">产品</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入标题" onChange={handleTitleChange} />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug (URL路径)"
            rules={[
              { required: true, message: '请输入Slug' },
              { pattern: /^[a-z0-9-]+$/, message: 'Slug只能包含小写字母、数字和连字符' },
            ]}
            extra="Slug用于URL路径，例如：example.com/pages/your-slug"
          >
            <Input placeholder="请输入Slug" />
          </Form.Item>

          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea rows={10} placeholder="请输入内容" />
          </Form.Item>

          <Form.Item
            name="meta_title"
            label="SEO标题 (可选)"
            extra="如果留空，将使用内容标题"
          >
            <Input placeholder="请输入SEO标题" />
          </Form.Item>

          <Form.Item
            name="meta_description"
            label="SEO描述 (可选)"
          >
            <TextArea rows={3} placeholder="请输入SEO描述" />
          </Form.Item>

          <Form.Item
            name="published"
            label="发布状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="已发布" unCheckedChildren="草稿" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Space>
              <Button onClick={() => navigate('/contents')}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ContentForm; 